import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, ChevronLeft, ChevronRight, MessageSquare, Calendar as CalendarIcon, Clock, TrendingUp, Cloud, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { fr } from "date-fns/locale";

// Helper to get the effective "today" date (day changes at 4 AM)
function getEffectiveToday() {
  const now = new Date();
  const parisTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
  const hours = parisTime.getHours();
  
  // If before 4 AM, we're still on "yesterday"
  if (hours < 4) {
    parisTime.setDate(parisTime.getDate() - 1);
  }
  
  return parisTime.toISOString().split('T')[0];
}

export default function RoutinePage() {
  const { authHeaders } = useAuth();
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getEffectiveToday());
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    fetchRoutine(selectedDate);
    fetchCalendarData();
  }, [selectedDate]);

  const fetchCalendarData = async () => {
    try {
      const response = await axios.get(`${API}/routine/calendar`, authHeaders());
      setCalendarData(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRoutine = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/routine/date/${date}`, authHeaders());
      setRoutine(response.data);
      setComment(response.data.comment || "");
    } catch (e) {
      console.error(e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleTaskToggle = async (taskKey, currentStatus, actualTime = null) => {
    const newStatus = !currentStatus;
    
    setRoutine(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => 
        t.task_key === taskKey ? { ...t, completed: newStatus, actual_time: actualTime } : t
      )
    }));

    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/routine/task`, {
        task_key: taskKey,
        completed: newStatus,
        date: selectedDate,
        actual_time: actualTime
      }, authHeaders());

      setRoutine(prev => ({
        ...prev,
        progress_percent: response.data.progress_percent
      }));

      setLastSaved(new Date());
      
      if (newStatus) {
        const messages = [
          "Super ! Continue comme ça !",
          "Bravo ! Un pas de plus !",
          "Génial ! Tu gères !",
          "Bien joué !",
          "Tu avances bien !"
        ];
        toast.success(messages[Math.floor(Math.random() * messages.length)]);
      }
    } catch (e) {
      setRoutine(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => 
          t.task_key === taskKey ? { ...t, completed: currentStatus } : t
        )
      }));
      toast.error("Erreur de mise à jour");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!confirm("Décocher toutes les tâches de ce jour ?")) return;
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/routine/reset-all`, {
        date: selectedDate
      }, authHeaders());
      
      // Refresh the routine
      await fetchRoutine(selectedDate);
      setLastSaved(new Date());
      toast.success("Toutes les tâches ont été décochées");
    } catch (e) {
      console.error(e);
      toast.error("Erreur");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveComment = async () => {
    try {
      await axios.post(`${API}/routine/comment`, {
        date: selectedDate,
        comment: comment
      }, authHeaders());
      toast.success("Commentaire sauvegardé");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleCalendarSelect = (date) => {
    if (date) {
      setSelectedDate(date.toISOString().split('T')[0]);
      setShowCalendar(false);
    }
  };

  const isToday = selectedDate === getEffectiveToday();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Create calendar progress map
  const calendarProgressMap = {};
  calendarData.forEach(item => {
    calendarProgressMap[item.date] = item.progress_percent;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-full border-4 border-[#A3E4D7]/30 border-t-[#A3E4D7]"
        />
      </div>
    );
  }

  // Separate tasks by category - handle old data without category
  const prioritaireTasks = routine?.tasks?.filter(t => t.category === "prioritaire") || [];
  const horaireTasks = routine?.tasks?.filter(t => t.category === "horaire") || [];
  const actionTasks = routine?.tasks?.filter(t => t.category === "action") || [];
  
  // If no categorized tasks, show all tasks as a fallback (old data)
  const hasCategories = prioritaireTasks.length > 0 || horaireTasks.length > 0 || actionTasks.length > 0;
  const allTasks = routine?.tasks || [];
  
  const requiredTasks = routine?.tasks?.filter(t => !t.optional) || [];
  const completedCount = requiredTasks.filter(t => t.completed).length;
  const totalRequired = requiredTasks.length;

  return (
    <div className="space-y-6" data-testid="routine-page">
      {/* Cloud sync indicator */}
      <div className="flex items-center justify-between text-xs text-[#7F8C8D]">
        <div className="flex items-center gap-2">
          <Cloud size={14} className={isSaving ? "text-[#FAD7A1] animate-pulse" : "text-[#A9DFBF]"} />
          <span>
            {isSaving ? "Sauvegarde..." : lastSaved ? `Sauvegardé à ${lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : "Synchronisé"}
          </span>
        </div>
        <Button
          onClick={handleResetAll}
          variant="ghost"
          size="sm"
          data-testid="reset-all-button"
          className="text-[#7F8C8D] hover:text-[#2C3E50] hover:bg-[#FAD7A1]/20 text-xs gap-1"
        >
          <RotateCcw size={12} />
          Tout décocher
        </Button>
      </div>

      {/* Header with date navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-nunito text-2xl font-bold text-[#2C3E50]">
            Ma Routine
          </h1>
          <Button
            onClick={() => setShowCalendar(!showCalendar)}
            variant="outline"
            size="icon"
            data-testid="toggle-routine-calendar"
            className="rounded-full border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
          >
            <CalendarIcon size={18} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => changeDate(-1)}
            variant="outline"
            size="icon"
            data-testid="prev-day-button"
            className="rounded-full border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-[#2C3E50] font-medium min-w-[140px] text-center text-sm">
            {isToday ? "Aujourd'hui" : formatDate(selectedDate)}
          </span>
          <Button
            onClick={() => changeDate(1)}
            variant="outline"
            size="icon"
            data-testid="next-day-button"
            className="rounded-full border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {showCalendar && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="bg-white rounded-3xl p-6 shadow-soft"
        >
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#A9DFBF]" />
                <span className="text-[#7F8C8D]">100%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#AED6F1]" />
                <span className="text-[#7F8C8D]">50%+</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#FAD7A1]" />
                <span className="text-[#7F8C8D]">Commencé</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={new Date(selectedDate)}
              onSelect={handleCalendarSelect}
              locale={fr}
              data-testid="routine-calendar"
              modifiers={{
                completed: (date) => {
                  const dateStr = date.toISOString().split('T')[0];
                  return calendarProgressMap[dateStr] === 100;
                },
                partial: (date) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const progress = calendarProgressMap[dateStr];
                  return progress !== undefined && progress >= 50 && progress < 100;
                },
                started: (date) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const progress = calendarProgressMap[dateStr];
                  return progress !== undefined && progress > 0 && progress < 50;
                }
              }}
              modifiersClassNames={{
                completed: "bg-[#A9DFBF] text-[#2C3E50] hover:bg-[#A9DFBF]/80",
                partial: "bg-[#AED6F1] text-[#2C3E50] hover:bg-[#AED6F1]/80",
                started: "bg-[#FAD7A1] text-[#2C3E50] hover:bg-[#FAD7A1]/80"
              }}
              components={{
                IconLeft: () => <ChevronLeft className="h-4 w-4" />,
                IconRight: () => <ChevronRight className="h-4 w-4" />
              }}
            />
          </div>
        </motion.div>
      )}

      {/* Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 shadow-soft"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[#FAD7A1]" size={20} />
            <span className="font-semibold text-[#2C3E50]">
              Progression du jour
            </span>
          </div>
          <span className="text-2xl font-bold text-[#A3E4D7]" data-testid="routine-progress">
            {completedCount}/{totalRequired}
          </span>
        </div>
        
        <div className="progress-track">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${routine?.progress_percent || 0}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="progress-fill"
          />
        </div>
        
        <p className="text-[#7F8C8D] text-sm mt-3">
          {routine?.progress_percent === 100 
            ? "Journée parfaite ! Tu es extraordinaire !"
            : routine?.progress_percent >= 50
              ? "Tu es sur la bonne voie !"
              : "Chaque petit geste compte !"}
        </p>
      </motion.div>

      {/* Quote */}
      {routine?.quote && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="quote-card text-center"
        >
          <p className="text-[#2C3E50] italic" data-testid="routine-quote">
            "{routine.quote}"
          </p>
        </motion.div>
      )}

      {/* Horaires Section - En premier */}
      {hasCategories ? (
        <>
          <div className="space-y-3">
            <h2 className="font-nunito font-bold text-[#2C3E50] text-lg px-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#AED6F1]"></span>
              Horaires
              <span className="text-xs text-[#7F8C8D] font-normal ml-1">(clique sur l'icône pour voir l'évolution)</span>
            </h2>
            <AnimatePresence>
              {horaireTasks.map((task, index) => (
                <TaskItem 
                  key={task.task_key} 
                  task={task} 
                  index={index} 
                  onToggle={handleTaskToggle}
                  selectedDate={selectedDate}
                  authHeaders={authHeaders()}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Prioritaire Section */}
          {prioritaireTasks.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-nunito font-bold text-[#2C3E50] text-lg px-1 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FAD7A1]"></span>
                Prioritaire
              </h2>
              <AnimatePresence>
                {prioritaireTasks.map((task, index) => (
                  <TaskItem 
                    key={task.task_key} 
                    task={task} 
                    index={index} 
                    onToggle={handleTaskToggle}
                    selectedDate={selectedDate}
                    authHeaders={authHeaders()}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Actions Section */}
          <div className="space-y-3">
            <h2 className="font-nunito font-bold text-[#2C3E50] text-lg px-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#A3E4D7]"></span>
              Actions
            </h2>
            <AnimatePresence>
              {actionTasks.map((task, index) => (
                <TaskItem 
                  key={task.task_key} 
                  task={task} 
                  index={index} 
                  onToggle={handleTaskToggle}
                  selectedDate={selectedDate}
                  authHeaders={authHeaders()}
                />
              ))}
            </AnimatePresence>
          </div>
        </>
      ) : (
        /* Fallback for old data without categories */
        <div className="space-y-3">
          <h2 className="font-nunito font-bold text-[#2C3E50] text-lg px-1">
            Mes tâches
          </h2>
          <AnimatePresence>
            {allTasks.map((task, index) => (
              <TaskItem 
                key={task.task_key} 
                task={task} 
                index={index} 
                onToggle={handleTaskToggle}
                selectedDate={selectedDate}
                authHeaders={authHeaders()}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Comment Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 shadow-soft"
      >
        <button
          onClick={() => setShowComment(!showComment)}
          className="w-full flex items-center gap-3 text-left"
          data-testid="toggle-comment-button"
        >
          <MessageSquare className="text-[#7F8C8D]" size={20} />
          <span className="font-semibold text-[#2C3E50]">
            Notes du jour
          </span>
          <ChevronRight 
            size={18} 
            className={`ml-auto text-[#7F8C8D] transition-transform ${showComment ? "rotate-90" : ""}`}
          />
        </button>
        
        {showComment && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-4 space-y-3"
          >
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comment s'est passée ta journée ?"
              data-testid="routine-comment-textarea"
              className="min-h-[100px] rounded-2xl border-2 border-[#A3E4D7]/30 focus:border-[#A3E4D7] bg-[#FAFCFB]"
            />
            <Button
              onClick={handleSaveComment}
              data-testid="save-comment-button"
              className="rounded-full bg-[#A3E4D7] hover:bg-[#A3E4D7]/80 text-[#2C3E50]"
            >
              Sauvegarder
            </Button>
          </motion.div>
        )}
      </motion.div>

      {/* Encouragement */}
      {completedCount === totalRequired && totalRequired > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-8"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#A9DFBF] to-[#A3E4D7] flex items-center justify-center">
            <Sparkles className="text-white" size={32} />
          </div>
          <p className="text-xl font-nunito font-bold text-[#2C3E50]">
            Félicitations !
          </p>
          <p className="text-[#7F8C8D] mt-2">
            Tu as complété toute ta routine !
          </p>
        </motion.div>
      )}
    </div>
  );
}

// Task Item Component
function TaskItem({ task, index, onToggle, selectedDate, authHeaders }) {
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [timeValue, setTimeValue] = useState(task.actual_time || "");
  const [showHistory, setShowHistory] = useState(false);
  const [timeHistory, setTimeHistory] = useState([]);

  const isHoraireTask = task.category === "horaire";

  const handleToggle = () => {
    if (isHoraireTask && !task.completed) {
      // For horaire tasks, show time picker first
      setShowTimePicker(true);
    } else {
      onToggle(task.task_key, task.completed, null);
    }
  };

  const handleTimeConfirm = async () => {
    if (timeValue) {
      // First complete the task
      onToggle(task.task_key, false, timeValue);
      setShowTimePicker(false);
      
      // Then check comparison with yesterday
      try {
        const response = await axios.get(
          `${API}/routine/compare-time/${task.task_key}/${selectedDate}/${timeValue}`,
          authHeaders
        );
        if (response.data.message) {
          // Show encouraging message after a short delay
          setTimeout(() => {
            toast.success(response.data.message, { duration: 4000 });
          }, 1500);
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      toast.error("Entre l'heure s'il te plaît");
    }
  };

  const handleSkipTime = () => {
    onToggle(task.task_key, task.completed, null);
    setShowTimePicker(false);
  };

  const fetchTimeHistory = async () => {
    try {
      const response = await axios.get(`${API}/routine/time-history/${task.task_key}`, authHeaders);
      setTimeHistory(response.data);
      setShowHistory(true);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-white rounded-2xl p-4 shadow-soft transition-all duration-300 ${
        task.completed ? "opacity-80" : ""
      } ${task.optional ? "border-l-4 border-[#FAD7A1]" : ""}`}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={handleToggle}
          data-testid={`task-${task.task_key}`}
          className="flex items-center gap-4 text-left flex-1"
        >
          <motion.div
            whileTap={{ scale: 0.9 }}
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
              task.completed 
                ? "bg-[#A9DFBF]" 
                : "border-2 border-[#A3E4D7] hover:bg-[#A3E4D7]/10"
            }`}
          >
            {task.completed && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500 }}
              >
                <Check className="text-[#2C3E50]" size={18} />
              </motion.div>
            )}
          </motion.div>
          
          <div className="flex-1">
            <span className={`text-[#2C3E50] ${
              task.completed ? "line-through text-[#7F8C8D]" : ""
            }`}>
              {task.label}
            </span>
            {task.optional && (
              <span className="ml-2 text-xs text-[#FAD7A1] font-medium">
                (optionnel)
              </span>
            )}
            {/* Show actual time if set */}
            {task.actual_time && task.completed && (
              <span className="ml-2 text-xs text-[#A3E4D7] font-medium">
                à {task.actual_time}
              </span>
            )}
          </div>
        </button>

        {/* History button for horaire tasks */}
        {isHoraireTask && (
          <button
            onClick={fetchTimeHistory}
            className="p-2 rounded-full text-[#7F8C8D] hover:bg-[#A3E4D7]/10 transition-all"
            title="Voir l'historique"
          >
            <TrendingUp size={16} />
          </button>
        )}
      </div>

      {/* Time picker modal for horaire tasks */}
      {showTimePicker && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 p-4 rounded-xl bg-[#FAFCFB]"
        >
          <p className="text-sm text-[#2C3E50] mb-3 flex items-center gap-2">
            <Clock size={16} className="text-[#A3E4D7]" />
            À quelle heure ?
          </p>
          <div className="flex items-center gap-3">
            <input
              type="time"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              className="flex-1 px-4 py-2 rounded-xl border-2 border-[#A3E4D7]/30 focus:border-[#A3E4D7] outline-none bg-white text-[#2C3E50]"
              data-testid={`time-input-${task.task_key}`}
            />
            <Button
              onClick={handleTimeConfirm}
              size="sm"
              className="rounded-full bg-[#A3E4D7] hover:bg-[#A3E4D7]/80 text-[#2C3E50]"
            >
              OK
            </Button>
            <Button
              onClick={handleSkipTime}
              size="sm"
              variant="outline"
              className="rounded-full border-[#7F8C8D]/30 text-[#7F8C8D]"
            >
              Passer
            </Button>
          </div>
        </motion.div>
      )}

      {/* Time history modal */}
      {showHistory && timeHistory.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 p-4 rounded-xl bg-[#FAFCFB]"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-[#2C3E50] flex items-center gap-2">
              <TrendingUp size={16} className="text-[#A3E4D7]" />
              Historique des horaires
            </p>
            <button
              onClick={() => setShowHistory(false)}
              className="text-[#7F8C8D] text-sm"
            >
              Fermer
            </button>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {timeHistory.slice(0, 7).map((entry, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-[#7F8C8D]">
                  {new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
                <span className="font-medium text-[#2C3E50]">{entry.actual_time}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {showHistory && timeHistory.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 p-4 rounded-xl bg-[#FAFCFB] text-center"
        >
          <p className="text-sm text-[#7F8C8D]">Pas encore d'historique</p>
          <button
            onClick={() => setShowHistory(false)}
            className="text-[#A3E4D7] text-sm mt-2"
          >
            Fermer
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}
